// The dashboard catalog — home. Reads the SAME query App.tsx's Boot() already
// resolved (`dashboardsQuery`), so this page never re-fetches on mount; it
// just renders what's already in cache (TanStack dedupes the identical query
// key regardless).
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { EmptyState, ErrorState, LoadingState } from "../components/states";
import { orderDashboards } from "../lib/catalog-order";
import { artifactsQuery, dashboardsQuery } from "../lib/queries";

export function CatalogPage() {
  const dashboards = useQuery(dashboardsQuery);
  // Documents load beside the catalog rather than gating it: a failure to
  // list them must not take the dashboard list down with it.
  const artifacts = useQuery(artifactsQuery);

  if (dashboards.isPending) return <LoadingState label="Loading dashboards…" />;
  if (dashboards.isError) {
    return <ErrorState error={dashboards.error} onRetry={() => void dashboards.refetch()} />;
  }

  // The backend returns these alphabetically by title, which answers "find me
  // the one called X" and nothing about which ones matter — it put "Assistant
  // Flow Funnel" and "CS Ops" above "Mira Product Measurement". `lib/catalog-
  // order.ts` leads with the dashboards the team actually opens; the rest keep
  // the alphabetical order they arrived in.
  const rows = orderDashboards(dashboards.data.dashboards);
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No dashboards yet"
        message="The BI registry hasn't been synced, or nothing in it is published."
      />
    );
  }

  return (
    <div className="bi-page">
      <div className="bi-page__head">
        <h1 className="bi-page__title">Dashboards</h1>
        <p className="bi-page__sub">
          {rows.length} dashboard{rows.length === 1 ? "" : "s"}
        </p>
      </div>
      <div className="bi-catalog-grid">
        {rows.map((d) => (
          <Link key={d.id} to="/d/$slug" params={{ slug: d.slug }} className="bi-catalog-card">
            <span className="bi-catalog-card__title">{d.title}</span>
            <span className="bi-catalog-card__meta">
              {d.chartCount} chart{d.chartCount === 1 ? "" : "s"}
            </span>
          </Link>
        ))}
      </div>

      {/* PUBLISHED DOCUMENTS. Listed here because a reader looking for an
          analysis should not have to know whether it was assembled from chart
          specs or written as one page — that distinction is ours, not theirs.
          Kept as its own group rather than mixed in, because the two behave
          differently once opened: a dashboard takes the filter bar, a document
          brings its own controls. */}
      {artifacts.data && artifacts.data.length > 0 ? (
        <>
          <div className="bi-page__head">
            <h2 className="bi-page__title">Documents</h2>
            <p className="bi-page__sub">
              Published analyses, served whole — their own layout and their own controls.
            </p>
          </div>
          <div className="bi-catalog-grid">
            {artifacts.data.map((a) => (
              <Link
                key={a.slug}
                to="/a/$slug"
                params={{ slug: a.slug }}
                className="bi-catalog-card"
              >
                <span className="bi-catalog-card__title">{a.title}</span>
                <span className="bi-catalog-card__meta">document</span>
              </Link>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
